import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from "react-router-dom";
import { API } from "aws-amplify";
import Fuse from 'fuse.js';
import swal from '@sweetalert/with-react';

import Button from '../../Button';
import SearchAddButton from '../../SearchAddButton';
import Dropdown from '../../Dropdown';
import UploadModal from '../../UploadModal';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTimes, faCheckSquare, faSearch, faSlidersH, faUpload, faPrint, faMagic, faEraser, faChevronLeft, faChevronRight, faMinusSquare, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';

import { JSONexists, getJSONFromStorage, useOutsideClick } from '../../../js/Helpers';
import { getLabelColorClass, getLabelTextColorClass, getLabelLinkColorClass } from '../utils/labelColorUtils';

const normalizeGradeValue = (grade) => {
	const grade_string = `${grade || ''}`.trim();
	const grade_match = grade_string.match(/\d+/);
	return grade_match ? `${parseInt(grade_match[0], 10)}` : grade_string;
};

const sortByLabelName = (label_a, label_b) => `${label_a.label || ''}`.localeCompare(`${label_b.label || ''}`);

const getDefaultTeamLabelMap = (students = [], labels = []) => {
	const grades = [...new Set(students.map(student => normalizeGradeValue(student.grade)).filter(Boolean))].sort((a, b) => Number(a) - Number(b));
	return grades.reduce((result, grade) => {
		const grade_prefix = `${grade}`.toLowerCase();
		const matching_labels = labels
			.filter(label_info => `${label_info.label || ''}`.trim().toLowerCase().startsWith(grade_prefix))
			.sort(sortByLabelName)
			.slice(0, 3)
			.map(label_info => `${label_info.label || ''}`.trim())
			.filter(Boolean);

		if(matching_labels.length > 0) result[grade] = matching_labels;
		return result;
	}, {});
};

const getDefaultRelatedArtsCourseIds = (courses = []) => {
	return courses
		.filter(course_info => `${course_info.elective}` === '1')
		.map(course_info => `${course_info.course_id}`);
};

function AutoCreateTeamsWorkflowModal({ mode, schoolInfo, students, courses, labels, onClose, onComplete }) {
	const [currentScreen, setCurrentScreen] = useState(1);
	const [teamLabelMap, setTeamLabelMap] = useState(() => getDefaultTeamLabelMap(students, labels));
	const [selectedClearLabelNames, setSelectedClearLabelNames] = useState([]);
	const [selectedCourseIds, setSelectedCourseIds] = useState(() => getDefaultRelatedArtsCourseIds(courses));
	const [courseSearchValue, setCourseSearchValue] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);

	const grades = useMemo(() => [...new Set(students.map(student => normalizeGradeValue(student.grade)).filter(Boolean))].sort((a, b) => Number(a) - Number(b)), [students]);
	const sortedLabels = useMemo(() => [...labels].sort(sortByLabelName), [labels]);
	const selectedTeamLabelCount = Object.values(teamLabelMap).reduce((total, grade_labels) => total + (Array.isArray(grade_labels) ? grade_labels.length : 0), 0);
	const selectedClearLabelCount = selectedClearLabelNames.length;
	const filteredCourses = useMemo(() => {
		const search_value = courseSearchValue.trim().toLowerCase();
		const sorted_courses = [...courses].sort((course_a, course_b) => `${course_a.course_code || ''}`.localeCompare(`${course_b.course_code || ''}`));
		if(!search_value) return sorted_courses;
		return sorted_courses.filter(course_info => `${course_info.name || ''} ${course_info.course_code || ''}`.toLowerCase().includes(search_value));
	}, [courses, courseSearchValue]);

	const toggleTeamLabel = (grade, label_name) => {
		setTeamLabelMap(previous_map => {
			const previous_grade_labels = Array.isArray(previous_map[grade]) ? previous_map[grade] : [];
			const label_exists = previous_grade_labels.includes(label_name);
			const next_grade_labels = label_exists ? previous_grade_labels.filter(existing_label => existing_label !== label_name) : [...previous_grade_labels, label_name];
			const next_map = {...previous_map};
			if(next_grade_labels.length > 0) next_map[grade] = next_grade_labels;
			else delete next_map[grade];
			return next_map;
		});
	};

	const toggleCourse = (course_id) => {
		setSelectedCourseIds(previous_ids => previous_ids.includes(`${course_id}`) ? previous_ids.filter(existing_id => existing_id !== `${course_id}`) : [...previous_ids, `${course_id}`]);
	};

	const toggleClearLabel = (label_name) => {
		setSelectedClearLabelNames(previous_labels => previous_labels.includes(label_name) ? previous_labels.filter(existing_label => existing_label !== label_name) : [...previous_labels, label_name]);
	};

	const validateCurrentScreen = () => {
		setErrorMessage(null);
		if(mode === 'clear' && selectedClearLabelCount === 0) {
			setErrorMessage('Choose at least one team to clear.');
			return false;
		}
		if(mode !== 'clear' && currentScreen === 1 && selectedTeamLabelCount === 0) {
			setErrorMessage('Choose at least one team label before continuing.');
			return false;
		}
		if(mode !== 'clear' && currentScreen === 2 && selectedCourseIds.length === 0) {
			setErrorMessage('Choose at least one related arts course before continuing.');
			return false;
		}
		return true;
	};

	const handleNext = () => {
		if(!validateCurrentScreen()) return;
		setCurrentScreen(currentScreen + 1);
	};

	const getWorkflowErrorMessage = () => {
		return mode === 'clear' ? 'There was an error while clearing teams. Please try again.' : 'There was an error while creating teams. Please try again.';
	};

	const submitWorkflow = async () => {
		if(mode === 'auto_create' && selectedCourseIds.length === 0) {
			setErrorMessage('Choose at least one related arts course before applying teams.');
			return;
		}

		setIsSaving(true);
		setErrorMessage(null);

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = mode === 'clear' ? '/admin/clear-team-labels' : '/admin/create-teams-from-requests';
		const body = {
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			data_version_id: schoolInfo.current_data_version_id,
			...(mode === 'clear' ? {
				team_labels: selectedClearLabelNames,
			} : {
				team_label_map: teamLabelMap,
				related_arts_course_ids: selectedCourseIds,
				include_alternates: false,
				apply_team_labels: true,
			}),
		};

		try {
			const response = await API.post(apiName, url, {response: true, body: JSON.stringify(body)});
			let response_data = response?.data;
			if(!response_data && mode === 'clear') {
				response_data = {
					status: 'cleared',
					mode: 'clear',
					save_result: {
						cleared_student_label_count: 0,
						team_labels: [],
					},
				};
			}
			if(!response_data || response_data.status === 'error') {
				setErrorMessage(getWorkflowErrorMessage());
				setIsSaving(false);
				return;
			}
			onClose();
			window.setTimeout(() => onComplete(response_data, mode), 0);
			return;
		}
		catch(e) {
			console.log(e);
			setErrorMessage(getWorkflowErrorMessage());
		}
		setIsSaving(false);
	};

	const renderClearLabelScreen = () => (
		<div className='auto-team-workflow-screen'>
			<h2>Choose Teams To Clear</h2>
			<p>Select the team labels whose student assignments should be removed. This does not delete the labels.</p>
			<div className='auto-team-label-grid auto-team-clear-label-grid'>
				{sortedLabels.map(label_info => {
					const label_name = `${label_info.label || ''}`.trim();
					const selected = selectedClearLabelNames.includes(label_name);
					return (
						<div className={`auto-team-choice-pill ${selected ? 'auto-team-choice-pill-selected' : ''}`} key={`clear-${label_info.label_id}`} onClick={() => toggleClearLabel(label_name)}>
							<div className={`create-labels-icon ${getLabelColorClass(label_info)}`}></div>
							<span>{label_name}</span>
						</div>
					);
				})}
			</div>
		</div>
	);

	const renderTeamLabelScreen = () => (
		<div className='auto-team-workflow-screen'>
			<h2>{mode === 'clear' ? 'Choose Teams To Clear' : 'Choose Teams By Grade'}</h2>
			<p>Select the labels this workflow is allowed to update. Existing student assignments are cleared only for these labels.</p>
			<div className='auto-team-grade-list'>
				{grades.map(grade => {
					const selected_labels = teamLabelMap[grade] || [];
					return (
						<div className='auto-team-grade-card' key={grade}>
							<div className='auto-team-grade-card-header'>
								<div>Grade {grade}</div>
								<div>{selected_labels.length} teams</div>
							</div>
							<div className='auto-team-label-grid'>
								{sortedLabels.map(label_info => {
									const label_name = `${label_info.label || ''}`.trim();
									const selected = selected_labels.includes(label_name);
									return (
										<div className={`auto-team-choice-pill ${selected ? 'auto-team-choice-pill-selected' : ''}`} key={`${grade}-${label_info.label_id}`} onClick={() => toggleTeamLabel(grade, label_name)}>
											<div className={`create-labels-icon ${getLabelColorClass(label_info)}`}></div>
											<span>{label_name}</span>
										</div>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);

	const renderCourseScreen = () => (
		<div className='auto-team-workflow-screen'>
			<h2>Choose Related Arts Courses</h2>
			<p>These courses define the request combos used to balance students onto teams.</p>
			<div className='auto-team-course-toolbar'>
				<div className='search-add-adding-input-container'>
					<input className='student-labels-search-input' value={courseSearchValue} placeholder='Search courses...' onChange={(e) => setCourseSearchValue(e.target.value)}/>
					<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
				</div>
				<div className='auto-team-course-count'>{selectedCourseIds.length} selected</div>
			</div>
			<div className='auto-team-course-list'>
				{filteredCourses.map(course_info => {
					const selected = selectedCourseIds.includes(`${course_info.course_id}`);
					return (
						<div className={`auto-team-course-row ${selected ? 'auto-team-course-row-selected' : ''}`} key={course_info.course_id} onClick={() => toggleCourse(course_info.course_id)}>
							<div>
								<div className='auto-team-course-name'>{course_info.name} ({course_info.course_code})</div>
								<div className='auto-team-course-meta'>{course_info.semester || 'No length'} / {`${course_info.elective}` === '1' ? 'Elective' : 'Non-elective'}</div>
							</div>
							{selected ? <FontAwesomeIcon className='fas-checkbox-checked' icon={faCheckSquare}/> : <FontAwesomeIcon className='fas-checkbox-unchecked' icon={faSquare}/>}
						</div>
					);
				})}
			</div>
		</div>
	);

	const renderReviewScreen = () => (
		<div className='auto-team-workflow-screen'>
			<h2>{mode === 'clear' ? 'Clear Teams' : 'Review And Apply'}</h2>
			<p>{mode === 'clear' ? 'This will remove student assignments from the selected team labels only.' : 'This will clear the selected team labels, create balanced teams, and save those labels to students.'}</p>
			<div className='auto-team-review-grid'>
				<div><strong>{selectedTeamLabelCount}</strong><span>team labels</span></div>
				{mode === 'auto_create' && <div><strong>{selectedCourseIds.length}</strong><span>related arts courses</span></div>}
				<div><strong>{Object.keys(teamLabelMap).length}</strong><span>grades</span></div>
			</div>
		</div>
	);

	const totalScreens = mode === 'clear' ? 1 : 3;
	const displayScreen = currentScreen;

	return (
		<div className='auto-team-workflow-overlay'>
			<div className='auto-team-workflow-modal'>
				<div className='auto-team-workflow-header'>
					<div>
						<div className='auto-team-workflow-title'>{mode === 'clear' ? 'Clear Teams' : 'Auto Create Teams'}</div>
						<div className='auto-team-workflow-step'>Step {currentScreen} of {totalScreens}</div>
					</div>
					<FontAwesomeIcon className='gray-link' icon={faTimes} onClick={onClose}/>
				</div>
				<div className='auto-team-workflow-body'>
					{mode === 'clear' && renderClearLabelScreen()}
					{mode !== 'clear' && displayScreen === 1 && renderTeamLabelScreen()}
					{displayScreen === 2 && renderCourseScreen()}
					{displayScreen === 3 && renderReviewScreen()}
					{errorMessage && <div className='auto-team-error-message'>{errorMessage}</div>}
				</div>
				<div className='auto-team-workflow-footer'>
					<button type='button' className='btn gray-btn' onClick={currentScreen === 1 ? onClose : () => setCurrentScreen(currentScreen - 1)} disabled={isSaving}>
						{currentScreen === 1 ? 'Cancel' : <><FontAwesomeIcon icon={faChevronLeft}/> Back</>}
					</button>
					{mode === 'clear' ?
						<button type='button' className='btn green-btn' onClick={() => validateCurrentScreen() && submitWorkflow()} disabled={isSaving || selectedClearLabelCount === 0}>
							{isSaving ? 'Working...' : `Clear ${selectedClearLabelCount} Team${selectedClearLabelCount === 1 ? '' : 's'}`}
						</button>
					:
					currentScreen < totalScreens ?
						<button type='button' className='btn blue-btn' onClick={handleNext} disabled={isSaving}>Next <FontAwesomeIcon icon={faChevronRight}/></button>
					:
						<button type='button' className='btn green-btn' onClick={submitWorkflow} disabled={isSaving}>
							{isSaving ? 'Working...' : (mode === 'clear' ? 'Clear Teams' : 'Apply Teams')}
						</button>
					}
				</div>
			</div>
		</div>
	);
}

function StudentLabelsPrintReport({ schoolInfo, scheduleInfo, labelGroups }) {
	const school_name = schoolInfo?.school_name || schoolInfo?.name || 'School';
	const schedule_name = scheduleInfo?.schedule_version_name || scheduleInfo?.name || '';
	const total_students_listed = labelGroups.reduce((total, label_group) => total + label_group.students.length, 0);

	return (
		<div className='student-labels-print-report'>
			<div className='student-labels-print-report-header'>
				<div>
					<div className='student-labels-print-report-title'>Student Teams / Labels</div>
					<div className='student-labels-print-report-subtitle'>{school_name}{schedule_name ? ` / ${schedule_name}` : ''}</div>
				</div>
				<div className='student-labels-print-report-meta'>{labelGroups.length} teams / {total_students_listed} student assignments</div>
			</div>
			<div className='student-labels-print-team-list'>
				{labelGroups.map(label_group => (
					<div className='student-labels-print-team' key={label_group.label_id}>
						<div className='student-labels-print-team-header'>
							<div>{label_group.label}</div>
							<div>{label_group.students.length} student{label_group.students.length === 1 ? '' : 's'}</div>
						</div>
						<div className='student-labels-print-student-list'>
							{label_group.students.length === 0 ?
								<div className='student-labels-print-empty-team'>No students</div>
							:
								label_group.students.map(student_info => (
									<div className='student-labels-print-student-row' key={`${label_group.label_id}-${student_info.student_id}`}>
										<span>{student_info.last_name}, {student_info.first_name}</span>
										<span>{student_info.grade ? `Grade ${student_info.grade}` : ''}</span>
									</div>
								))
							}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

const StudentLabelsStudentCard = React.memo(function StudentLabelsStudentCard({ studentInfo, labelOptions, onToggleStudentLabel }) {
	const student_grade = studentInfo.grade;

	return (
		<div className='student-labels-student-container'>
			<h2 className='student-labels-student-container-heading'>{studentInfo.last_name}, {studentInfo.first_name}</h2>
			<div className='student-labels-student-container-grade'>{student_grade && `Grade ${student_grade}`}</div>
			<div className='student-labels-student-labels-parent-container'>
				<div className='small-text student-labels-student-labels-check-message'>Check all that apply:</div>
				<div>
					{labelOptions.length === 0 ? <div className='student-labels-no-label-options'>No teams/labels added yet...</div> : labelOptions.map(label_info => {
						const has_label = studentInfo.student_labels.findIndex(label => label.label_id === label_info.label_id) !== -1;
						return (
							<div className='student-labels-label-option student-labels-label-option-clickable' key={`${studentInfo.student_id}-${label_info.label_id}`} onClick={() => onToggleStudentLabel(studentInfo, label_info.label_id)}>
								<div className={`create-labels-icon ${getLabelColorClass(label_info)}`}></div>
								<div>{label_info.label}</div>
								{has_label ? <FontAwesomeIcon className='fas-checkbox-checked' icon={faCheckSquare}/> : <FontAwesomeIcon className='fas-checkbox-unchecked' icon={faSquare}/>}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
});

export default function StudentLabels({ isLoading, setIsLoading, subscreenRestricted, subscreenLocked, getNewData, userInfo, schoolType, schoolInfo, scheduleInfo, studentsLoading, students, setStudents, coursesLoading, courses, sectionsLoading, sections, labels, handleMoveToNextScreen, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo }) {
	const filter_ref = useRef();
	const student_labels_menu_ref = useRef();

	const [isSearching, setIsSearching] = useState(false);
	const [currentView, setCurrentView] = useState(null);
	const [currentOpenStudentLabelsMenu, setCurrentOpenStudentLabelsMenu] = useState(null);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [autoTeamWorkflowMode, setAutoTeamWorkflowMode] = useState(null);
	const [searchValue, setSearchValue] = useState('');
	const [labelOptions, setLabelOptions] = useState([]);
	const [labelListsLoaded, setLabelListsLoaded] = useState(false);
	const [pendingLabelActions, setPendingLabelActions] = useState({});
	const [selectedTeamStudentIds, setSelectedTeamStudentIds] = useState({});
	const [maxStudentsToShow, setMaxStudentsToShow] = useState(50);
	const [filterDataVisible, setFilterDataVisible] = useState(false);
	const [filterOptions, setFilterOptions] = useState({});

	const handleSearchChange = (e) => {
		setMaxStudentsToShow(50);
		setSearchValue(e.target.value);
		setIsSearching(false);
	};

	const showMoreStudents = () => {
		setMaxStudentsToShow(maxStudentsToShow + 50);
	};

	const toggleFilterOptions = () => {
		setFilterDataVisible(!filterDataVisible);
	};

	const updateFilterOptions = (filter_title, option_index, selected) => {
		filterOptions[filter_title][option_index].selected = selected;
		setFilterOptions({...filterOptions});
	};

	useOutsideClick(filter_ref, () => {
		setFilterDataVisible(false);
	});

	useOutsideClick(student_labels_menu_ref, () => {
		setCurrentOpenStudentLabelsMenu(null);
	});

	const toggleStudentLabelsMenuItem = (menu_item) => {
		setCurrentOpenStudentLabelsMenu(currentOpenStudentLabelsMenu === menu_item ? null : menu_item);
	};

	const handlePrint = () => {
		setCurrentOpenStudentLabelsMenu(null);
		const cleanupPrintClass = () => {
			document.body.classList.remove('student-labels-printing');
			window.removeEventListener('afterprint', cleanupPrintClass);
		};

		document.body.classList.add('student-labels-printing');
		window.addEventListener('afterprint', cleanupPrintClass);
		window.setTimeout(() => window.print(), 0);
	};

	const openAutoTeamWorkflow = (mode) => {
		setCurrentOpenStudentLabelsMenu(null);
		setAutoTeamWorkflowMode(mode);
	};

	const removeClearedTeamLabelsFromStudents = useCallback((team_labels = []) => {
		const cleared_label_ids = new Set(team_labels.map(label_info => `${label_info.label_id || ''}`).filter(Boolean));
		if(cleared_label_ids.size === 0) return;

		setStudents((previous_students) => previous_students.map(student_info => {
			const student_labels = Array.isArray(student_info.student_labels) ? student_info.student_labels : [];
			const next_student_labels = student_labels.filter(student_label => !cleared_label_ids.has(`${student_label.label_id}`));
			if(next_student_labels.length === student_labels.length) return student_info;
			return {...student_info, student_labels: next_student_labels};
		}));

		setSelectedTeamStudentIds((previous_selections) => {
			const next_selections = {...previous_selections};
			cleared_label_ids.forEach(label_id => delete next_selections[label_id]);
			return next_selections;
		});
	}, [setStudents]);

	const applySavedTeamAssignmentsToStudents = useCallback((team_labels = [], assignments = []) => {
		const cleared_label_ids = new Set(team_labels.map(label_info => `${label_info.label_id || ''}`).filter(Boolean));
		const assignments_by_student_id = assignments.reduce((result, assignment_info) => {
			const student_id = `${assignment_info.student_id || ''}`;
			const label_id = `${assignment_info.label_id || ''}`;
			if(!student_id || !label_id) return result;
			if(!result[student_id]) result[student_id] = [];
			result[student_id].push({label_id});
			return result;
		}, {});

		if(cleared_label_ids.size === 0 && Object.keys(assignments_by_student_id).length === 0) return;

		setStudents((previous_students) => previous_students.map(student_info => {
			const student_id = `${student_info.student_id}`;
			const student_labels = Array.isArray(student_info.student_labels) ? student_info.student_labels : [];
			const next_student_labels = student_labels.filter(student_label => !cleared_label_ids.has(`${student_label.label_id}`));
			(assignments_by_student_id[student_id] || []).forEach(assignment_label => {
				if(next_student_labels.findIndex(student_label => `${student_label.label_id}` === `${assignment_label.label_id}`) === -1) next_student_labels.push(assignment_label);
			});
			if(next_student_labels.length === student_labels.length && !assignments_by_student_id[student_id]) return student_info;
			return {...student_info, student_labels: next_student_labels};
		}));

		setSelectedTeamStudentIds((previous_selections) => {
			const next_selections = {...previous_selections};
			cleared_label_ids.forEach(label_id => delete next_selections[label_id]);
			return next_selections;
		});
	}, [setStudents]);

	const handleAutoTeamWorkflowComplete = (response_data, workflow_mode) => {
		const save_result = response_data?.save_result || {};
		if(workflow_mode === 'clear') removeClearedTeamLabelsFromStudents(save_result.team_labels || []);
		else applySavedTeamAssignmentsToStudents(save_result.team_labels || [], save_result.student_label_assignments || []);
		getNewData();

		const cleared_count = Number(save_result.cleared_student_label_count || 0);
		const assigned_count = Number(save_result.assigned_student_count || 0);
		const clear_text = cleared_count === 0 ? 'No student team assignments needed to be cleared.' : `Cleared ${cleared_count} student team assignment${cleared_count === 1 ? '' : 's'}.`;
		const create_text = `Saved ${assigned_count} student team assignment${assigned_count === 1 ? '' : 's'}.`;

		swal({
			icon: "success",
			title: "Success!",
			text: workflow_mode === 'clear' ? clear_text : create_text,
			button: "OK",
		});
	};

	const toggleSearch = (index) => {
		const is_open = ('search_open' in labelOptions[index]);
		if(is_open)
		{
			delete labelOptions[index].search_open;
			delete labelOptions[index].student_suggestions;
		}
		else
		{
			labelOptions[index].search_open = true;
			labelOptions[index].student_suggestions = [...students].sort((a, b) => {
				const last_name_sort = `${a.last_name || ''}`.localeCompare(`${b.last_name || ''}`);
				if(last_name_sort !== 0) return last_name_sort;
				return `${a.first_name || ''}`.localeCompare(`${b.first_name || ''}`);
			});
		}
		setLabelOptions([...labelOptions]);
	};

	const searchStudents = (e, index) => {
		const search_value = e.target.value;
		if(search_value.length === 0)
		{
			labelOptions[index].student_suggestions = [...students].sort((a, b) => {
				const last_name_sort = `${a.last_name || ''}`.localeCompare(`${b.last_name || ''}`);
				if(last_name_sort !== 0) return last_name_sort;
				return `${a.first_name || ''}`.localeCompare(`${b.first_name || ''}`);
			});
			setLabelOptions([...labelOptions]);
			return;
		}
		const fuse = new Fuse(students, {
			keys: ['first_name', 'last_name'],
			threshold: 0.6
		});
		const results = fuse.search(search_value);
		labelOptions[index].student_suggestions = results.map(result => result.item);
		setLabelOptions([...labelOptions]);
	};

	const updateDatabase = useCallback(async (update_type, student_id_or_ids, label_id) => {
		const student_ids = Array.isArray(student_id_or_ids) ? student_id_or_ids : [student_id_or_ids];
		const data = {
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			update_type,
			label_id,
			...(Array.isArray(student_id_or_ids) ? {student_ids} : {student_id: student_id_or_ids}),
		};

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-student-label';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			await API.post(apiName, url, myInit);
			return true;
		} catch(e)
		{
			console.log(e.response);
			return false;
		}
	}, [schoolInfo.school_id, schoolInfo.current_schedule_version_id]);

	const applyStudentLabelUpdate = useCallback((update_type, student_id, label_id) => {
		setStudents((previous_students) => {
			const student_index = previous_students.findIndex(student => student.student_id === student_id);
			if(student_index === -1) return previous_students;

			const student_info = previous_students[student_index];
			const student_labels = [...student_info.student_labels];
			const student_label_index = student_labels.findIndex(label => label.label_id === label_id);

			if(update_type === 'add' && student_label_index === -1) student_labels.push({label_id});
			if(update_type === 'remove' && student_label_index !== -1) student_labels.splice(student_label_index, 1);

			const next_students = [...previous_students];
			next_students[student_index] = {...student_info, student_labels};
			return next_students;
		});
	}, [setStudents]);

	const updateStudentLabel = useCallback((update_type, student_id, label_id) => {
		if(update_type === 'remove') {
			setSelectedTeamStudentIds((previous_selections) => {
				const label_key = `${label_id}`;
				const next_label_selections = (previous_selections[label_key] || []).filter(existing_student_id => existing_student_id !== `${student_id}`);
				const next_selections = {...previous_selections};
				if(next_label_selections.length > 0) next_selections[label_key] = next_label_selections;
				else delete next_selections[label_key];
				return next_selections;
			});
		}

		applyStudentLabelUpdate(update_type, student_id, label_id);

		// Let React paint the optimistic UI update before doing the network write.
		window.requestAnimationFrame(() => {
			updateDatabase(update_type, student_id, label_id);
		});
	}, [applyStudentLabelUpdate, updateDatabase]);

	const updateStudentLabelWithFeedback = useCallback((update_type, student_id, label_id) => {
		const action_key = `${student_id}-${label_id}`;
		if(pendingLabelActions[action_key]) return;

		if(update_type === 'remove') {
			setSelectedTeamStudentIds((previous_selections) => {
				const label_key = `${label_id}`;
				const next_label_selections = (previous_selections[label_key] || []).filter(existing_student_id => existing_student_id !== `${student_id}`);
				const next_selections = {...previous_selections};
				if(next_label_selections.length > 0) next_selections[label_key] = next_label_selections;
				else delete next_selections[label_key];
				return next_selections;
			});
		}

		setPendingLabelActions((previous_actions) => ({...previous_actions, [action_key]: update_type}));

		return new Promise((resolve) => {
			window.requestAnimationFrame(async () => {
				const update_successful = await updateDatabase(update_type, student_id, label_id);
				if(update_successful) applyStudentLabelUpdate(update_type, student_id, label_id);

				setPendingLabelActions((previous_actions) => {
					const next_actions = {...previous_actions};
					delete next_actions[action_key];
					return next_actions;
				});
				resolve(update_successful);
			});
		});
	}, [pendingLabelActions, updateDatabase, applyStudentLabelUpdate]);

	const toggleStudentLabel = useCallback((student_info, label_id) => {
		const has_label = student_info.student_labels.findIndex(label => label.label_id === label_id) !== -1;
		updateStudentLabel(has_label ? 'remove' : 'add', student_info.student_id, label_id);
	}, [updateStudentLabel]);

	const toggleTeamStudentSelection = useCallback((label_id, student_id) => {
		const label_key = `${label_id}`;
		const student_key = `${student_id}`;
		setSelectedTeamStudentIds((previous_selections) => {
			const previous_label_selections = previous_selections[label_key] || [];
			const student_is_selected = previous_label_selections.includes(student_key);
			const next_label_selections = student_is_selected ? previous_label_selections.filter(existing_student_id => existing_student_id !== student_key) : [...previous_label_selections, student_key];
			const next_selections = {...previous_selections};
			if(next_label_selections.length > 0) next_selections[label_key] = next_label_selections;
			else delete next_selections[label_key];
			return next_selections;
		});
	}, []);

	const toggleAllTeamStudents = useCallback((label_id, student_ids, should_select) => {
		const label_key = `${label_id}`;
		const next_student_ids = [...new Set(student_ids.map(student_id => `${student_id}`))];
		setSelectedTeamStudentIds((previous_selections) => {
			const next_selections = {...previous_selections};
			if(should_select && next_student_ids.length > 0) next_selections[label_key] = next_student_ids;
			else delete next_selections[label_key];
			return next_selections;
		});
	}, []);

	const deleteSelectedStudentsFromTeam = useCallback((label_id, student_ids) => {
		const label_key = `${label_id}`;
		const student_id_set = new Set(student_ids.map(student_id => `${student_id}`));
		if(student_id_set.size === 0) return;

		setSelectedTeamStudentIds((previous_selections) => {
			const next_selections = {...previous_selections};
			delete next_selections[label_key];
			return next_selections;
		});

		setStudents((previous_students) => previous_students.map(student_info => {
			if(!student_id_set.has(`${student_info.student_id}`)) return student_info;
			const student_labels = Array.isArray(student_info.student_labels) ? student_info.student_labels : [];
			const next_student_labels = student_labels.filter(student_label => `${student_label.label_id}` !== label_key);
			if(next_student_labels.length === student_labels.length) return student_info;
			return {...student_info, student_labels: next_student_labels};
		}));

		window.requestAnimationFrame(async () => {
			const update_successful = await updateDatabase('remove', [...student_id_set], label_id);
			if(!update_successful) getNewData();
		});
	}, [setStudents, updateDatabase, getNewData]);

	useEffect(() => {
		if(!studentsLoading && !coursesLoading && !sectionsLoading)
		{
			const sorted_labels = [...labels].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
			setLabelOptions([...sorted_labels]);
			setLabelListsLoaded(true);
			setIsLoading(false);
		}
		else
		{
			setLabelListsLoaded(false);
		}
	}, [studentsLoading, coursesLoading, sectionsLoading, labels, students, setIsLoading, courses, sections]);

	useEffect(() => {
		if(currentView) sessionStorage.setItem('student_label_view', currentView);
	}, [currentView]);

	useEffect(() => {
		if(studentsLoading) return;
		const grades = students.reduce((result, student) => {
			const student_grade = student.grade;
			if(result.findIndex(option => option.value === student_grade) === -1) result.push({display:student_grade, value:student_grade, selected:false});
			return result;
		}, []);
		const sorted_grades = [...grades].sort((a, b) => Number(a.value) - Number(b.value));
		setFilterOptions({grades: sorted_grades});
	}, [studentsLoading, students]);

	useEffect(() => {
		if(!userInfo || subscreenRestricted || subscreenLocked) return;
		setScreenAllowsUniversalSidebar(true);
		setUniversalSidebarLoadingInfo({sidebar_name:"Student Labels Notes", sidebar_type:'notes', subscreen:'student_labels', department_id:null, user_id:userInfo.user_id, help_link_path:`/app/${schoolType}/documentation/scheduling_wizard/create_sections/student_labels`});
	}, [userInfo, subscreenRestricted, subscreenLocked, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo, schoolType]);

	useEffect(() => {
		const current_view = JSONexists('student_label_view', 'session') ? getJSONFromStorage('student_label_view', false, 'session') : 'label';
		setCurrentView(current_view);
	}, []);

	const current_user_is_admin = (userInfo.role_id === '1' || userInfo.role_id === '2' || userInfo.role_id === '5');
	const viewOptions = [{value:'label', display:'By Teams/Labels'}, {value:'student', display:'By Student'}];
	const labelListsAreLoading = !labelListsLoaded || studentsLoading || coursesLoading || sectionsLoading;
	const studentsByID = useMemo(() => {
		return students.reduce((result, student_info) => {
			result[`${student_info.student_id}`] = student_info;
			return result;
		}, {});
	}, [students]);
	const printLabelGroups = useMemo(() => {
		return [...labelOptions].sort(sortByLabelName).map(label_info => {
			const label_id = `${label_info.label_id}`;
			const students_with_label = students.filter(student_info => {
				const student_labels = Array.isArray(student_info.student_labels) ? student_info.student_labels : [];
				return student_labels.findIndex(student_label => `${student_label.label_id}` === label_id) !== -1;
			}).sort((student_a, student_b) => {
				const last_name_compare = `${student_a.last_name || ''}`.localeCompare(`${student_b.last_name || ''}`);
				if(last_name_compare !== 0) return last_name_compare;
				const first_name_compare = `${student_a.first_name || ''}`.localeCompare(`${student_b.first_name || ''}`);
				if(first_name_compare !== 0) return first_name_compare;
				return `${student_a.student_id || ''}`.localeCompare(`${student_b.student_id || ''}`);
			});

			return {
				label_id,
				label: label_info.label || '(No Name)',
				students: students_with_label,
			};
		});
	}, [labelOptions, students]);
	const normalizedSearchValue = searchValue.trim().toLowerCase();
	const filteredStudents = useMemo(() => {
		if(normalizedSearchValue.length <= 1) return students;
		return students.filter(student_info => `${student_info.last_name || ''} ${student_info.first_name || ''}`.toLowerCase().includes(normalizedSearchValue));
	}, [students, normalizedSearchValue]);
	const filteredLabelEntries = useMemo(() => {
		return labelOptions.map((label_info, index) => ({label_info, index})).filter(({label_info}) => {
			if(normalizedSearchValue.length <= 1) return true;
			return `${label_info.label || ''}`.toLowerCase().includes(normalizedSearchValue);
		});
	}, [labelOptions, normalizedSearchValue]);
	const searchPlaceholder = currentView === 'student' ? 'Search for students...' : 'Search for teams/labels...';

	let selected_filters = [];
	Object.keys(filterOptions).forEach(filter_title => {
		filterOptions[filter_title].forEach(filter_option => {
			if(filter_option.selected) selected_filters.push(filter_option);
		});
	});

	let student_counter = 1;

	return (
		<>
		{showUploadModal &&
			<UploadModal schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} uploadTitle='Teams/Labels' uploadAPIName='teamlabels_placeholder' closeModal={() => setShowUploadModal(false)} handlePullNewData={getNewData}/>
		}
		{autoTeamWorkflowMode &&
			<AutoCreateTeamsWorkflowModal
				mode={autoTeamWorkflowMode}
				schoolInfo={schoolInfo}
				students={students}
				courses={courses}
				labels={labels}
				onClose={() => setAutoTeamWorkflowMode(null)}
				onComplete={handleAutoTeamWorkflowComplete}
			/>
		}
		<StudentLabelsPrintReport schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} labelGroups={printLabelGroups}/>
		<h1 className='progress-main-header'>Student Labels</h1>
		<p className='progress-subheader' style={{maxWidth:'450px'}}>Add your labels from the previous step to specific students to start grouping/teaming them.</p>
		{(isLoading) ? (
			<div className='center-text'><img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'40px'}}/></div>
		) : (subscreenRestricted) ? (
			<div className='general-screen-message'><FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/><h1>No Access</h1><div>You do not have access to this screen</div></div>
		) : (subscreenLocked) ? (
			<div className='general-screen-message'><FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/><h1>Temporarily Locked</h1><div>This screen is locked until all other prior steps are completed</div></div>
		) : (current_user_is_admin) ? (
			<div id='student-labels-main-container'>
				<div className='section-review-menu-row student-labels-menu-row'>
					<div className='section-review-menu-bar' ref={student_labels_menu_ref}>
						<div className='section-review-menu-btn' onClick={() => toggleStudentLabelsMenuItem('file')}>
							<span>File</span>
							{currentOpenStudentLabelsMenu === 'file' &&
								<div className='section-review-menu-dropdown-options-container'>
									<div className='section-review-menu-dropdown-option' onClick={handlePrint}><FontAwesomeIcon icon={faPrint}/> Print</div>
									<div className='section-review-menu-dropdown-option' onClick={() => { setCurrentOpenStudentLabelsMenu(null); setShowUploadModal(true); }}><FontAwesomeIcon icon={faUpload}/> Upload from file...</div>
								</div>
							}
						</div>
						<div className='section-review-menu-btn' onClick={() => toggleStudentLabelsMenuItem('actions')}>
							<span>Actions</span>
							{currentOpenStudentLabelsMenu === 'actions' &&
								<div className='section-review-menu-dropdown-options-container'>
									<div className='section-review-menu-dropdown-option' onClick={() => openAutoTeamWorkflow('auto_create')}><FontAwesomeIcon icon={faMagic}/> Auto Create Teams</div>
									<div className='section-review-menu-dropdown-option' onClick={() => openAutoTeamWorkflow('clear')}><FontAwesomeIcon icon={faEraser}/> Clear Teams</div>
								</div>
							}
						</div>
					</div>
				</div>
				<div className='student-labels-options-row'>
					<div className='student-labels-search-column'>
						<div className='mb-search-bar-outer-container'>
							<div className='mb-search-bar-container' style={{width:'100%'}}>
								<input className='mb-search-bar' placeholder={searchPlaceholder} value={searchValue} onChange={handleSearchChange} />
								<FontAwesomeIcon className='mb-search-bar-icon' icon={faSearch}/>
								{currentView === 'student' &&
									<div className='mb-search-bar-filter-container' ref={filter_ref}>
										<div style={{display:'grid',width:'100%', height:'100%', alignItems:'center', justifyItems:'center'}} onClick={toggleFilterOptions}><FontAwesomeIcon icon={faSlidersH}/></div>
										{filterDataVisible &&
											<div className='list-filter-options-container click-restricted' style={{top:'60px'}}>
												{Object.keys(filterOptions).map((filter_title, j) => (
													<div key={j}>
														<div className='capitalize'>{filter_title}</div>
														{filterOptions[filter_title].map((filter_option, i) => (
															<div className='parent-div list-filter-option click-restricted' key={i} onClick={() => updateFilterOptions(filter_title, i, !filter_option.selected)}>
																<div className='list-filter-option-name click-restricted'>{filter_option.display}</div>
																{filter_option.selected ? <FontAwesomeIcon className='fas-checkbox-checked click-restricted' icon={faCheckSquare}/> : <FontAwesomeIcon className='fas-checkbox-unchecked click-restricted' icon={faSquare}/>}
															</div>
														))}
													</div>
												))}
											</div>
										}
									</div>
								}
							</div>
						</div>
						{currentView === 'student' &&
							<div className='mb-selected-filters-bar student-labels-selected-filters-bar'>
								{selected_filters.length > 0 && <><div className='mb-selected-filters-text'>Filters:</div>{Object.keys(filterOptions).map((filter_title, i) => (<React.Fragment key={i}>{filterOptions[filter_title].map((filter_option, i) => filter_option.selected ? <div className='mb-selected-filters-filter-tag' key={i}><div className='mb-selected-filters-filter-tag-inner-container'><div>{filter_option.display}</div><FontAwesomeIcon className='dark-gray-link' icon={faTimes} onClick={() => updateFilterOptions(filter_title, i, false)} /></div></div> : null)}</React.Fragment>))}</>}
							</div>
						}
					</div>
					<div id='student-label-department-dropdown'><Dropdown data={viewOptions} currentValue={currentView} handleChange={(new_value) => { setCurrentView(new_value); setSearchValue(''); setMaxStudentsToShow(50); }} /></div>
				</div>
				{currentView === 'student' ? (
					<>
						{labelListsAreLoading ? <div className='student-labels-student-container'><div className='center-text' style={{padding:'20px 0px'}}><img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'30px'}}/></div></div> : isSearching ? <div className='center-text'><img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'100px'}}/></div> : (
							<>
								{filteredStudents.map((student_info) => {
									if(student_counter > maxStudentsToShow) return null;
									const student_id = student_info.student_id;
									const student_is_allowed = (selected_filters.length > 0) ? filterOptions.grades.findIndex(grade_option => grade_option.value === student_info.grade && grade_option.selected) !== -1 : true;
									if(!student_is_allowed) return null;
									student_counter++;
										return <StudentLabelsStudentCard key={student_id} studentInfo={student_info} labelOptions={labelOptions} onToggleStudentLabel={toggleStudentLabel} />;
									})}
								{(student_counter > maxStudentsToShow) && <div className='btn blue-btn load-more-btn' onClick={showMoreStudents}>Load More Students</div>}
							</>
						)}
					</>
				) : (
					<div>
						{labelListsAreLoading ? <div className='student-labels-student-container'><div className='center-text' style={{padding:'30px'}}><img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'30px'}}/></div></div> : (
							labelOptions.length === 0 ? <div className='student-labels-no-label-options' style={{padding:'30px'}}>No teams/labels added yet...</div> : filteredLabelEntries.length === 0 ? <div className='student-labels-no-label-options' style={{padding:'30px'}}>No teams/labels match your search.</div> : filteredLabelEntries.map(({label_info, index}) => {
								const label_id = `${label_info.label_id}`;
								const label_color_class = getLabelColorClass(label_info);
								const label_link_class = getLabelLinkColorClass(label_info);
								const students_with_label = students.filter(student => student.student_labels.findIndex(label => `${label.label_id}` === label_id) !== -1);
								const visible_student_ids = students_with_label.map(student_info => `${student_info.student_id}`);
								const selected_student_id_set = new Set((selectedTeamStudentIds[label_id] || []).filter(student_id => visible_student_ids.includes(`${student_id}`)).map(student_id => `${student_id}`));
								const selected_student_ids = [...selected_student_id_set];
								const selected_student_count = selected_student_ids.length;
								const all_students_selected = students_with_label.length > 0 && selected_student_count === students_with_label.length;
								const student_search_open = ('search_open' in label_info);
								const student_suggestions = ('student_suggestions' in label_info) ? label_info.student_suggestions : [];
								return (
									<div className='student-labels-student-container' key={label_info.label_id}>
										<h2 className='student-labels-student-container-heading'>{label_info.label}</h2>
										<div className={`student-labels-student-count ${getLabelTextColorClass(label_info)}`}>{students_with_label.length} student(s)</div>
										{students_with_label.length > 0 &&
											<div className='select-all-row'>
												<div className={`select-all-container ${selected_student_count > 0 && 'blue-text'}`} onClick={() => toggleAllTeamStudents(label_id, visible_student_ids, !all_students_selected)}>
													<FontAwesomeIcon icon={selected_student_count > 0 ? faMinusSquare : faSquare}/>
													<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
												</div>
												{selected_student_count > 0 &&
													<div className='small-text'>
														<div className='red-link' onClick={() => deleteSelectedStudentsFromTeam(label_id, selected_student_ids)}>Delete {selected_student_count} student{selected_student_count === 1 ? '' : 's'}</div>
													</div>
												}
											</div>
										}
										{students_with_label.length === 0 && <div className='student-labels-no-label-options'>No students added yet...</div>}
										{students_with_label.map(student_info => {
											const student_id = `${student_info.student_id}`;
											const student_is_selected = selected_student_id_set.has(student_id);
											const action_key = `${student_id}-${label_id}`;
											const row_loading = pendingLabelActions[action_key] === 'remove';

											if(row_loading)
											{
												return (
													<div className='student-labels-label-option student-labels-label-option-loading' key={action_key}>
														<img src={require('../../../images/balls.gif')} alt='removing...' style={{height:'28px'}}/>
													</div>
												);
											}

											return (
												<div className={`student-labels-label-option student-labels-team-student-option ${student_is_selected ? 'student-labels-team-student-option-selected' : ''}`} key={action_key}>
													<FontAwesomeIcon className={student_is_selected ? 'fas-checkbox-checked student-labels-team-student-checkbox' : 'fas-checkbox-unchecked student-labels-team-student-checkbox'} icon={student_is_selected ? faCheckSquare : faSquare} onClick={() => toggleTeamStudentSelection(label_id, student_id)}/>
													<div className={`create-labels-icon ${label_color_class}`}></div>
													<div>{student_info.last_name}, {student_info.first_name}</div>
													<FontAwesomeIcon className='gray-link' icon={faTimes} onClick={() => updateStudentLabelWithFeedback('remove', student_id, label_id)}/>
												</div>
											);
										})}
										<div className='student-labels-add-container'><div className={`${label_link_class} student-labels-add-student-link`} onClick={() => toggleSearch(index)}>Add Student</div></div>
										{student_search_open && (
											<>
												<div className='search-add-search-container'>
													<div className='search-add-adding-input-container'>
														<input className='student-labels-search-input' placeholder='Search for a student' onChange={(e) => searchStudents(e, index)} autoFocus={true}/>
														<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
													</div>
													<FontAwesomeIcon className='gray-link' icon={faTimes} onClick={() => toggleSearch(index)}/>
												</div>
												<div className='section-review-search-add-search-results'>
													{student_suggestions.length !== 0 &&
														<>
															<div className='search-add-did-you-mean'>Did you mean:</div>
															{student_suggestions.map((student, i) => {
																const student_id = student.student_id;
																const action_loading = !!pendingLabelActions[`${student_id}-${label_info.label_id}`];
																const live_student_info = studentsByID[`${student_id}`] || student;
																const student_has_label = Array.isArray(live_student_info.student_labels) ? live_student_info.student_labels.findIndex(label => label.label_id === label_info.label_id) !== -1 : false;

																return (
																	<div className='search-add-search-result' key={i}>
																		<div>{student.last_name}, {student.first_name}</div>
																		<div>
																			<SearchAddButton
																				selected={student_has_label}
																				loading={action_loading}
																				onAdd={() => updateStudentLabelWithFeedback('add', student_id, label_info.label_id)}
																				className={label_color_class}
																			/>
																		</div>
																	</div>
																);
															})}
														</>
													}
												</div>
											</>
										)}
									</div>
								);
							})
						)}
					</div>
				)}
			</div>
		) : (userInfo && !current_user_is_admin) ? (
			<div className='locked-screen-notification-container'>
				<FontAwesomeIcon className='blue-text' icon={faLock} style={{fontSize:'50px'}}/>
				<h1>Locked</h1>
				<p>This step is only accessible to the master scheduler. You should be notified when future steps are unlocked!</p>
				<Link to='/progress' className='blue-text'>Go Back</Link>
			</div>
		) : null}
		{(!subscreenLocked && !subscreenRestricted && current_user_is_admin) &&
			<div className='fixed-bottom-save-container'>
				<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step: Section Labels' onClickFunction={handleMoveToNextScreen}/>
			</div>
		}
		</>
	);
}
